export const STEP_TYPE_LABELS: Record<string, string> = {
  'call-erp-endpoint': 'Call ERP Endpoint',
  'erp-login': 'ERP Login',
  'collect-user-input': 'Collect User Input',
  'send-email': 'Send Email',
  'generate-report': 'Generate Report',
  'call-ai-agent': 'Call AI Agent',
  'fail-workflow': 'Fail Workflow',
};

export function getStepTypeLabel(stepType: string): string {
  return STEP_TYPE_LABELS[stepType] ?? stepType
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface CallErpEndpointConfig {
  erp_slug: string;
  endpoint_slug: string;
  body_mapping: Record<string, unknown>;
}

export interface ErpLoginConfig {
  erp_slug: string;
  uid?: string;
  password?: string;
  language?: string;
}

export interface CollectUserInputConfig {
  fields: string[];
  validation?: Record<string, string>;
}

export interface SendEmailConfig {
  to: string;
  template: string;
  subject?: string;
  data_keys?: string[];
}

export interface GenerateReportConfig {
  report_type: 'csv' | 'json' | 'pdf';
  filename: string;
  data_key: string;
  storage_disk?: string;
  storage_path?: string;
}

export interface CallAiAgentConfig {
  agent_slug: string;
  prompt_template: string;
  input_keys?: string[];
  output_key?: string;
}

export interface FailWorkflowConfig {
  /** Error message the workflow halts with; supports `{{context.path}}` templates. */
  message: string;
}

export type StepConfig =
  | CallErpEndpointConfig
  | ErpLoginConfig
  | CollectUserInputConfig
  | SendEmailConfig
  | GenerateReportConfig
  | CallAiAgentConfig
  | FailWorkflowConfig;

export interface KeyValuePair {
  key: string;
  value: string;
}

const KV_FIELDS = new Set(['body_mapping', 'validation']);
const ARRAY_FIELDS = new Set(['fields', 'data_keys', 'input_keys']);

// Values whose textual form starts with '{' or '[' are tried as JSON so that
// nested objects/arrays in body_mapping (e.g. task-new's `entity`) round-trip
// as structured data instead of stringifying to "[object Object]". A failed
// parse falls back to the raw string — this preserves template-style values
// like "{{template}}" that look JSON-like but aren't.
function parseJsonIfShaped(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return raw;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

function kvPairsToRecord(pairs: KeyValuePair[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const pair of pairs) {
    if (pair.key) {
      result[pair.key] = parseJsonIfShaped(pair.value);
    }
  }
  return result;
}

function recordToKvPairs(record: Record<string, unknown>): KeyValuePair[] {
  return Object.entries(record).map(([key, value]) => ({
    key,
    value:
      typeof value === 'string' ? value : JSON.stringify(value, null, 2),
  }));
}

export function serializeConfig(
  _stepType: string,
  formValues: Record<string, unknown>,
): Record<string, unknown> {
  const config: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(formValues)) {
    if (!key.startsWith('config_')) continue;

    const configKey = key.slice('config_'.length);

    if (KV_FIELDS.has(configKey) && Array.isArray(value)) {
      config[configKey] = kvPairsToRecord(value as KeyValuePair[]);
    } else if (ARRAY_FIELDS.has(configKey) && Array.isArray(value)) {
      config[configKey] = (value as string[]).filter((v) => v !== '');
    } else if (value === '') {
      // Skip empty strings so optional fields are absent rather than stored as ""
      continue;
    } else {
      config[configKey] = value;
    }
  }

  return config;
}

export function deserializeConfig(
  _stepType: string,
  config: Record<string, unknown>,
): Record<string, unknown> {
  const formValues: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(config)) {
    const formKey = `config_${key}`;

    if (
      KV_FIELDS.has(key) &&
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      formValues[formKey] = recordToKvPairs(value as Record<string, unknown>);
    } else if (ARRAY_FIELDS.has(key)) {
      formValues[formKey] = Array.isArray(value) ? (value as string[]) : [];
    } else {
      formValues[formKey] = value;
    }
  }

  return formValues;
}
